import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Button,
  Group,
  Badge,
  ActionIcon,
  Tooltip,
  Stack,
  Table,
  TextInput,
  Select,
  Pagination,
  Text,
  Loader,
  Center,
  Paper,
  Code,
} from '@mantine/core';
import {
  IconPlus,
  IconTrash,
  IconEye,
  IconSearch,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { differenceInHours } from 'date-fns';
import type { Environment } from '@shared/types';
import { api } from '@/services/api';
import { EnvironmentStatusBadge } from '@/components/EnvironmentStatusBadge';
import { formatDateDistance, parseUTCDate } from '@/utils/date';
import { DeleteEnvironmentModal } from '@/components/DeleteEnvironmentModal';
import { AutoRefreshControls } from '@/components/AutoRefreshControls';
import { useAutoRefresh } from '@/hooks/useAutoRefresh';

const PAGE_SIZE = 50;

export function EnvironmentsPage() {
  const navigate = useNavigate();
  const { enabled: autoRefresh, setEnabled: setAutoRefresh, refetchInterval } = useAutoRefresh({ interval: 5000 });
  const [selectedEnvironment, setSelectedEnvironment] = useState<Environment | null>(null);
  const [deleteModalOpened, deleteModalHandlers] = useDisclosure(false);
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch environments
  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['environments'],
    queryFn: () => api.listEnvironments({ limit: 100 }),
    refetchInterval,
  });

  const environments = data?.items || [];

  // Filter and search environments
  const filteredEnvironments = useMemo(() => {
    let filtered = environments;

    // Apply search
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter((env) =>
        env.name.toLowerCase().includes(searchLower) ||
        env.namespace.toLowerCase().includes(searchLower) ||
        env.id.toLowerCase().includes(searchLower) ||
        env.owner_github_login?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter((env) => env.status === statusFilter);
    }

    return filtered;
  }, [environments, search, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredEnvironments.length / PAGE_SIZE);
  const paginatedEnvironments = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filteredEnvironments.slice(start, end);
  }, [filteredEnvironments, currentPage]);

  // Reset to page 1 when filters change
  useMemo(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  if (isLoading) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Environments</Title>
          <Group gap="xs">
            <AutoRefreshControls
              enabled={autoRefresh}
              onEnabledChange={setAutoRefresh}
              intervalLabel="(5s)"
            />
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => navigate('/environments/create')}
            >
              Create Environment
            </Button>
          </Group>
        </Group>

        <Paper p="md" withBorder>
          <Group gap="md">
            <TextInput
              placeholder="Search environments..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Select
              placeholder="Filter by status"
              clearable
              value={statusFilter}
              onChange={setStatusFilter}
              data={[
                { value: 'ready', label: 'Ready' },
                { value: 'pending', label: 'Pending' },
                { value: 'creating', label: 'Creating' },
                { value: 'failed', label: 'Failed' },
                { value: 'deleting', label: 'Deleting' },
              ]}
              style={{ width: 200 }}
            />
          </Group>
        </Paper>

        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Namespace</Table.Th>
                <Table.Th>Expires</Table.Th>
                <Table.Th>Owner</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {paginatedEnvironments.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text ta="center" c="dimmed" py="xl">
                      {search || statusFilter
                        ? 'No environments match your filters'
                        : 'No environments yet. Create one to get started!'}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                paginatedEnvironments.map((env) => {
                  const expiresAt = parseUTCDate(env.expires_at);
                  const hoursUntilExpiry = expiresAt
                    ? differenceInHours(expiresAt, new Date())
                    : null;
                  const isExpiringSoon =
                    hoursUntilExpiry !== null && hoursUntilExpiry < 24 && hoursUntilExpiry > 0;
                  const isExpired = hoursUntilExpiry !== null && hoursUntilExpiry <= 0;

                  return (
                    <Table.Tr
                      key={env.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/environments/${env.id}`)}
                    >
                      <Table.Td>
                        <Text fw={500}>{env.name}</Text>
                      </Table.Td>
                      <Table.Td>
                        <EnvironmentStatusBadge status={env.status} />
                      </Table.Td>
                      <Table.Td>
                        <Code>{env.namespace}</Code>
                      </Table.Td>
                      <Table.Td>
                        {expiresAt ? (
                          <Badge
                            color={isExpired ? 'red' : isExpiringSoon ? 'yellow' : 'gray'}
                            variant="light"
                          >
                            {isExpired
                              ? 'Expired'
                              : formatDateDistance(env.expires_at)}
                          </Badge>
                        ) : (
                          <Text c="dimmed">—</Text>
                        )}
                      </Table.Td>
                      <Table.Td>{env.owner_github_login || '—'}</Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Group gap="xs" wrap="nowrap">
                          <Tooltip label="View Details">
                            <ActionIcon
                              variant="subtle"
                              onClick={() => navigate(`/environments/${env.id}`)}
                            >
                              <IconEye size={18} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Delete">
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() => {
                                setSelectedEnvironment(env);
                                deleteModalHandlers.open();
                              }}
                            >
                              <IconTrash size={18} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })
              )}
            </Table.Tbody>
          </Table>

          {totalPages > 1 && (
            <Group justify="center" p="md">
              <Pagination
                total={totalPages}
                value={currentPage}
                onChange={setCurrentPage}
              />
            </Group>
          )}
        </Paper>

        <Text size="sm" c="dimmed">
          Showing {paginatedEnvironments.length} of {filteredEnvironments.length} environments
          {filteredEnvironments.length !== environments.length &&
            ` (filtered from ${environments.length} total)`}
        </Text>

        <DeleteEnvironmentModal
          environment={selectedEnvironment}
          opened={deleteModalOpened}
          onClose={() => {
            deleteModalHandlers.close();
            setSelectedEnvironment(null);
          }}
        />
      </Stack>
    </Container>
  );
}
